import React from 'react';
import { motion } from 'framer-motion';
import { Droplets, Gauge, Heart, Moon, Flame, Leaf } from 'lucide-react';

const BenefitsGrid = () => {
  const benefits = [
    {
      icon: Droplets,
      title: 'Flush Out Toxins Naturally',
      description: 'Deep cellular cleansing removes accumulated waste and environmental toxins safely.',
      gradient: 'from-blue-400 to-cyan-400',
    },
    {
      icon: Gauge,
      title: 'Boost Metabolism & Absorption',
      description: 'Kickstart your digestive fire and enhance nutrient uptake for optimal health.',
      gradient: 'from-orange-400 to-yellow-400',
    },
    {
      icon: Heart,
      title: 'Improve Heart & Liver Health',
      description: 'Support your vital organs with a gentle detox that promotes long-term wellness.',
      gradient: 'from-red-400 to-pink-400',
    },
    {
      icon: Moon,
      title: 'Restore Hormonal Balance',
      description: 'Regulate sleep cycles and hormonal rhythms for deeper rest and rejuvenation.',
      gradient: 'from-indigo-400 to-purple-400',
    },
    {
      icon: Flame,
      title: 'Revive Energy & Vitality',
      description: 'Feel recharged with sustained energy, improved mood, and mental clarity.',
      gradient: 'from-amber-400 to-orange-500',
    },
    {
      icon: Leaf,
      title: 'Prevent Stones Naturally',
      description: 'Natural cleansing helps prevent kidney and gallbladder stone formation.',
      gradient: 'from-green-400 to-emerald-400',
    },
  ];

  return (
    <div className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Why Your Body Needs This
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A holistic approach to wellness that addresses modern lifestyle challenges
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group"
              >
                <div className="bg-gradient-to-br from-purple-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border border-purple-100">
                  {/* Icon with gradient background */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    {benefit.title}
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>

                  {/* Hover indicator */}
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 + 0.3 }}
                    className={`h-1 bg-gradient-to-r ${benefit.gradient} rounded-full mt-6`}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <button
            onClick={() => {
              const timelineSection = document.getElementById('journey-timeline');
              timelineSection?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 text-lg font-semibold transition-colors group"
          >
            <span>See How It Works</span>
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              →
            </motion.span>
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default BenefitsGrid;
